import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Col, DefaultTheme, Div, Icon, Input, Label, Row, Switch, Text } from '@pinpoint/design-kit';

import { Button } from 'src/components';
import { RBAC } from 'src/config';
import { fetchClientSftp, saveClientSftp } from 'src/store/api';
import { setSftpAccess } from 'src/store/sftp_access';

function SftpTextarea({ testId, value, placeholder, disabled, onChange, grow }) {
  const fieldRef = useRef(null);
  const [size, setSize] = useState(null);
  const { colors, rounded, fontFamily, textSize } = DefaultTheme;

  useEffect(() => {
    const field = fieldRef.current;
    if (!grow || !field) return undefined;
    const observer = new ResizeObserver(() => {
      const { offsetWidth, offsetHeight } = field;
      if (!offsetWidth || !offsetHeight) return;
      setSize((current) => {
        if (current && current.width === offsetWidth && current.height === offsetHeight) return current;
        return { width: offsetWidth, height: offsetHeight };
      });
    });
    observer.observe(field);
    return () => observer.disconnect();
  }, [grow]);

  return (
    <textarea
      ref={fieldRef}
      data-test={testId}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={onChange}
      onFocus={(event) => {
        const field = event.currentTarget;
        field.style.borderColor = colors.gray700;
      }}
      onBlur={(event) => {
        const field = event.currentTarget;
        field.style.borderColor = colors.gray500;
      }}
      style={{
        boxSizing: 'border-box',
        display: 'block',
        width: grow ? (size ? `${size.width}px` : 'min(33rem, calc(100vw - 3rem))') : '100%',
        height: grow && size ? `${size.height}px` : undefined,
        maxWidth: grow ? 'calc(100vw - 3rem)' : '100%',
        minHeight: '6rem',
        marginTop: '0.5rem',
        padding: '8px 12px',
        resize: grow ? 'both' : 'vertical',
        overflow: 'auto',
        border: `1px solid ${colors.gray500}`,
        borderRadius: rounded.md,
        background: disabled ? colors.gray200 : colors.white,
        color: disabled ? colors.medium : colors.dark,
        cursor: disabled ? 'not-allowed' : 'text',
        fontFamily: fontFamily.primary,
        fontSize: textSize.size.body,
        lineHeight: textSize.height.body,
        fontWeight: 500,
      }}
    />
  );
}

SftpTextarea.propTypes = {
  testId: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  grow: PropTypes.bool,
};

SftpTextarea.defaultProps = {
  placeholder: '',
  disabled: false,
  grow: false,
};

function ClientSftp({ tenant, clientName, enqueueNotification, variant, onLoaded }) {
  const [allowedCidr, setAllowedCidr] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [configShown, setConfigShown] = useState(false);
  const [loginName, setLoginName] = useState('');
  const [saving, setSaving] = useState(false);
  const [sshPublicKey, setSshPublicKey] = useState('');
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const profile = useSelector((state) => state.app.profile) || {};
  const canEditLogin = [profile.role, profile.siteRole].some((role) => RBAC.CAN_EDIT_SFTP.includes(role));

  useEffect(() => {
    if (!tenant) return undefined;
    let active = true;
    fetchClientSftp(tenant, clientName)
      .then((data) => {
        if (!active) return;
        setEnabled(!!data.enabled);
        setAllowedCidr(data.allowedCidr || '');
        setLoginName(data.transferUserName || '');
        setSshPublicKey(data.sshPublicKey || '');
        setLoaded(true);
        setSftpAccess(tenant, !!data.enabled);
        if (onLoadedRef.current) onLoadedRef.current(data);
      })
      .catch((error) => {
        if (!active) return;
        enqueueNotification(error.message || 'Unable to load SFTP settings');
      });
    return () => {
      active = false;
    };
  }, [tenant, clientName, enqueueNotification]);

  useEffect(() => {
    if (variant !== 'login' || !configOpen) {
      setConfigShown(false);
      return undefined;
    }
    const frame = requestAnimationFrame(() => setConfigShown(true));
    const onKey = (event) => {
      if (event.key === 'Escape') setConfigOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
    };
  }, [configOpen, variant]);

  const applySaved = (saved) => {
    setEnabled(!!saved.enabled);
    setAllowedCidr(saved.allowedCidr || '');
    setLoginName(saved.transferUserName || '');
    setSshPublicKey(saved.sshPublicKey || '');
    setSftpAccess(tenant, !!saved.enabled);
    if (onLoadedRef.current) onLoadedRef.current(saved);
  };

  const onToggle = async () => {
    if (saving) return;
    const next = !enabled;
    setSaving(true);
    try {
      const saved = await saveClientSftp(tenant, { enabled: next, clientName });
      applySaved(saved);
      enqueueNotification(next ? 'SFTP allowed' : 'SFTP turned off', { autoClose: true });
    } catch (error) {
      enqueueNotification(error.message || 'Unable to save SFTP settings');
    } finally {
      setSaving(false);
    }
  };

  const onSaveLogin = async () => {
    setSaving(true);
    try {
      const saved = await saveClientSftp(tenant, {
        enabled,
        allowedCidr: allowedCidr.trim(),
        clientName,
        username: loginName.trim(),
        sshPublicKey: sshPublicKey.trim(),
      });
      applySaved(saved);
      enqueueNotification('SFTP login saved', { autoClose: true });
    } catch (error) {
      enqueueNotification(error.message || 'Unable to save SFTP settings');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  if (variant === 'login') {
    const { headerHeight } = DefaultTheme.application;
    return (
      <>
        <Button
          buttonStyle="primary"
          data-test="sftp-configuration"
          data-tip="Edit the SFTP login"
          m={{ r: '0.75rem' }}
          onClick={() => setConfigOpen(true)}
        >
          <Icon name="Settings" color="white" m={{ r: '0.5rem' }} viewBox="0 0 24 24" size="1rem" />
          CONFIGURATION
        </Button>
        {configOpen ? (
          <>
            <Div
              pos="fixed"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="black"
              opacity="0.2"
              zIndex="1000"
              cursor="pointer"
              onClick={() => setConfigOpen(false)}
            />
            <Div
              role="dialog"
              aria-label="SFTP configuration"
              data-test="sftp-configuration-panel"
              pos="fixed"
              right="0"
              bg="white"
              zIndex="1001"
              overflow="auto"
              shadow="5"
              p={{ x: '1.5rem', y: '1.25rem' }}
              style={{
                top: headerHeight,
                height: `calc(100vh - ${headerHeight})`,
                width: 'fit-content',
                minWidth: 'min(36rem, 100vw)',
                maxWidth: '100vw',
                boxSizing: 'border-box',
                transform: configShown ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 240ms ease-in-out',
              }}
            >
              <Div d="flex" justify="space-between" align="center">
                <Button
                  buttonStyle="secondary"
                  rounded="circle"
                  w="2.5rem"
                  h="2.4rem"
                  p="0rem"
                  data-tip="Hide"
                  aria-label="Hide configuration"
                  onClick={() => setConfigOpen(false)}
                >
                  <Icon color="white" name="RightArrow" />
                </Button>
                <Text textColor="navyBlue" textSize="title">
                  SFTP configuration
                </Text>
              </Div>
              <Text textSize="small" m={{ t: '0.75rem' }}>
                Server sftp.onpoint.pinpoint.ai
              </Text>
              <Div m={{ t: '1.25rem' }}>
                <Text textSize="input">Username</Text>
                <Input
                  data-test="sftp-username"
                  value={loginName}
                  placeholder="quanata"
                  disabled={!canEditLogin}
                  onChange={(event) => setLoginName(event.target.value.toLowerCase())}
                  m={{ t: '0.5rem' }}
                />
              </Div>
              <Div m={{ t: '1.25rem' }}>
                <Text textSize="input">Public key</Text>
                <SftpTextarea
                  grow
                  testId="sftp-public-key"
                  value={sshPublicKey}
                  placeholder="ssh-ed25519 AAAA..."
                  disabled={!canEditLogin}
                  onChange={(event) => setSshPublicKey(event.target.value)}
                />
              </Div>
              <Div m={{ t: '1.25rem' }}>
                <Text textSize="input">Public IPs</Text>
                <SftpTextarea
                  testId="sftp-public-ip"
                  value={allowedCidr}
                  placeholder={'203.0.113.10\n198.51.100.8'}
                  disabled={!canEditLogin}
                  onChange={(event) => setAllowedCidr(event.target.value)}
                />
                <Text textSize="small" m={{ t: '0.5rem' }}>
                  One public IP or CIDR per line. Each address opens port 22 on the shared security group.
                </Text>
              </Div>
              {canEditLogin ? (
                <Div d="flex" justify="flex-end" m={{ t: '1.25rem' }}>
                  <Button buttonStyle="primary" onClick={onSaveLogin} data-test="sftp-save" disabled={saving}>
                    <Text color="gray400">SAVE SFTP</Text>
                    {saving ? <Icon color="gray400" name="Loading" m={{ x: '0.5rem' }} /> : null}
                  </Button>
                </Div>
              ) : null}
            </Div>
          </>
        ) : null}
      </>
    );
  }

  if (variant === 'toggle') {
    return (
      <Row p={{ t: '0.75rem' }} align="center" justify="flex-end" data-test="client-sftp">
        <Col size={{ xs: '12', lg: '6' }}>
          <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
            <Text textSize="input">Allow SFTP</Text>
          </Div>
        </Col>
        <Col size={{ xs: '12', lg: '6' }}>
          <Label
            tag="div"
            align="center"
            justify="flex-end"
            data-test="sftp-enabled"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onToggle();
            }}
          >
            <Switch checked={enabled} activeColor="brand500" activeShadow="0" />
          </Label>
        </Col>
      </Row>
    );
  }

  return null;
}

ClientSftp.propTypes = {
  tenant: PropTypes.string,
  clientName: PropTypes.string,
  enqueueNotification: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['toggle', 'login']),
  onLoaded: PropTypes.func,
};

ClientSftp.defaultProps = {
  tenant: '',
  clientName: '',
  variant: 'toggle',
  onLoaded: undefined,
};

export default ClientSftp;
